import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { leadApi, leadSetupApi } from "../../pages/services/leadService";
import { FollowUpInput, errorText, fmtDateTime, fromLocalInput } from "./leadUtils";

// Bulk actions on the leads selected in the list: change their status, or send
// them a custom message. Both go through the same paths as one lead's page —
// a status change sends that status's messages and queues demo reminders.
// Messages are queued on the server and sent by the scheduler; these dialogs
// run it straight away and keep running it until the queue is empty (a closed
// page leaves the rest to the 5-minute run).
//
// leads: [{ _id, fullName, courseId, courseTitle, status }]

const BATCH = 25;   // leads per bulk-update request — well inside the API's time limit

// Run the scheduler until nothing is left to send. Messages go out in batches
// (up to 100 per UraanWapp request); one run sends at a time, so while another
// run (the 5-minute schedule) holds the queue, wait for it. Messages UraanWapp
// was too busy to take are tried again a few minutes later by the scheduler.
// onProgress({ sent, failed, skipped, retrying, due }).
const wait = (ms) => new Promise((ok) => setTimeout(ok, ms));
export async function drainQueue(onProgress) {
  const total = { sent: 0, failed: 0, skipped: 0, retrying: 0, due: 0 };
  let idle = 0;
  for (let round = 0; round < 60; round++) {
    const r = await leadApi.runAutomation();
    total.sent += r.sent || 0;
    total.failed += r.failed || 0;
    total.skipped += r.skipped || 0;
    total.retrying += r.retrying || 0;
    total.due = r.due || 0;
    onProgress({ ...total });
    if (!total.due) break;
    if (r.busy) { if (++idle > 10) break; await wait(4000); continue; }
    idle = 0;
    if (!((r.sent || 0) + (r.failed || 0) + (r.skipped || 0) + (r.cancelled || 0) + (r.retrying || 0))) break;
  }
  return total;
}

const Modal = ({ title, onClose, children, busy }) => (
  <div className="ld-overlay" onMouseDown={(e) => !busy && e.target === e.currentTarget && onClose()}>
    <div className="ld-modal" role="dialog" aria-modal="true" aria-label={title}>
      <div className="ld-modal-head"><h3>{title}</h3><button type="button" className="ld-btn ld-btn-sm" onClick={onClose} disabled={busy}>Close</button></div>
      <div className="ld-modal-body">{children}</div>
    </div>
  </div>
);

const Progress = ({ label, done, total }) => (
  <div className="ld-progress">
    <div className="ld-progress-label"><span>{label}</span><span>{total ? `${done} / ${total}` : ""}</span></div>
    <div className="ld-progress-bar"><i style={{ width: `${total ? Math.round((done / total) * 100) : 100}%` }} /></div>
  </div>
);

// The outcome: counts, then the leads that were skipped or failed, and why.
const Results = ({ rows, okLabel, sending }) => {
  const by = (r) => rows.filter((x) => x.result === r);
  const ok = rows.filter((x) => x.result === "changed" || x.result === "queued");
  const problems = [...by("failed"), ...by("skipped")];
  return (
    <>
      <div className="ld-bulk-counts">
        <span className="is-ok"><b>{ok.length}</b> {okLabel}</span>
        {by("skipped").length > 0 && <span><b>{by("skipped").length}</b> skipped</span>}
        {by("failed").length > 0 && <span className="is-bad"><b>{by("failed").length}</b> failed</span>}
      </div>
      {sending && (
        <div className="ld-sub" style={{ margin: "6px 0" }}>
          Messages: <b>{sending.sent}</b> sent{sending.failed ? `, ${sending.failed} failed` : ""}{sending.skipped ? `, ${sending.skipped} skipped` : ""}
          {sending.retrying ? ` — ${sending.retrying} will be tried again in a few minutes (UraanWapp was busy)` : ""}
          {sending.due ? ` — ${sending.due} still queued; the scheduler sends them within a few minutes.` : ""}
        </div>
      )}
      {problems.length > 0 && (
        <details className="ld-bulk-problems" open={problems.length <= 8}>
          <summary>Skipped or failed ({problems.length})</summary>
          <ul>{problems.map((p) => <li key={p._id}><Link to={`/leads/${p._id}`}>{p.fullName || p._id}</Link> — {p.reason}</li>)}</ul>
        </details>
      )}
    </>
  );
};

// ─── Change status ────────────────────────────────────────────────────────────
export const BulkStatusDialog = ({ leads, meta, onClose }) => {
  const [status, setStatus] = useState("");
  const [summary, setSummary] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [sessions, setSessions] = useState([]);
  const [chosenSessions, setChosenSessions] = useState({});      // courseId → sessionId
  const [error, setError] = useState("");
  const [phase, setPhase] = useState("form");                    // form | saving | sending | done
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState([]);
  const [sending, setSending] = useState(null);

  const def = meta.statuses.find((s) => s.key === status);
  const already = leads.filter((l) => l.status === status).length;
  const courses = useMemo(() => {
    const m = new Map();
    for (const l of leads) {
      const k = String(l.courseId);
      if (!m.has(k)) m.set(k, { _id: k, courseTitle: l.courseTitle || "Course", leads: 0 });
      m.get(k).leads += 1;
    }
    return [...m.values()];
  }, [leads]);
  useEffect(() => {
    if (status === "ready_for_demo" && !sessions.length) leadSetupApi.demoSessions({ upcoming: "1" }).then(setSessions).catch(() => {});
  }, [status, sessions.length]);

  const final = ["registered", "not_interested", "not_qualified"].includes(status);
  const needsFollowUp = !!def?.followUp;
  const missingSession = status === "ready_for_demo" && courses.some((c) => !chosenSessions[c._id]);
  const ready = status && summary.trim().length >= 3 && (!needsFollowUp || followUp) && !missingSession && already < leads.length;

  const run = async () => {
    setError("");
    setPhase("saving");
    const ids = leads.filter((l) => l.status !== status).map((l) => l._id);
    const skippedAlready = leads.filter((l) => l.status === status).map((l) => ({ _id: l._id, fullName: l.fullName, result: "skipped", reason: `Already ${def.label}` }));
    const out = [...skippedAlready];
    setProgress({ done: 0, total: ids.length });
    try {
      for (let i = 0; i < ids.length; i += BATCH) {
        const part = await leadApi.bulkUpdate({
          ids: ids.slice(i, i + BATCH), status, summary,
          followUpAt: needsFollowUp || followUp ? fromLocalInput(followUp) : undefined,
          demoSessions: status === "ready_for_demo" ? chosenSessions : undefined,
        });
        out.push(...part);
        setResults([...out]);
        setProgress({ done: Math.min(i + BATCH, ids.length), total: ids.length });
      }
    } catch (e) {
      setError(`Stopped: ${errorText(e)} — the leads above were already changed.`);
    }
    setResults(out);
    setPhase("sending");
    try { setSending(await drainQueue(setSending)); } catch (e) { setError(errorText(e)); }
    setPhase("done");
  };

  const busy = phase === "saving" || phase === "sending";
  return (
    <Modal title={`Change status of ${leads.length} lead${leads.length === 1 ? "" : "s"}`} onClose={() => onClose(phase !== "form")} busy={busy}>
      {error && <div className="ld-alert ld-alert-error">{error}</div>}
      {phase === "form" ? (
        <>
          <label className="ld-field">
            <span>New status<em>*</em></span>
            <select className="ld-select" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Choose a status…</option>
              {meta.statuses.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            {def?.hint && <span className="ld-field-hint">{def.hint}</span>}
          </label>

          {status === "ready_for_demo" && (
            <div className="ld-field">
              <span>Demo session{courses.length > 1 ? " — one for each course" : ""}<em>*</em></span>
              {courses.map((c) => {
                const options = sessions.filter((s) => String(s.courseId) === c._id);
                return (
                  <div key={c._id} className="ld-bulk-course">
                    <span className="ld-sub">{c.courseTitle} · {c.leads} lead{c.leads === 1 ? "" : "s"}</span>
                    <select className="ld-select" value={chosenSessions[c._id] || ""} onChange={(e) => setChosenSessions((m) => ({ ...m, [c._id]: e.target.value }))}>
                      <option value="">{options.length ? "Choose a session…" : "No upcoming session for this course"}</option>
                      {options.map((s) => <option key={s._id} value={s._id}>{fmtDateTime(s.startsAt)}{s.title ? ` — ${s.title}` : ""}</option>)}
                    </select>
                  </div>
                );
              })}
              {courses.some((c) => !sessions.some((s) => String(s.courseId) === c._id)) && (
                <span className="ld-field-hint">Add sessions in <Link to="/leads/setup?tab=demos">Lead settings → Demo sessions</Link>.</span>
              )}
            </div>
          )}

          {status && !final && (
            <div className="ld-field">
              <span>Next follow-up{needsFollowUp && <em>*</em>}</span>
              <FollowUpInput value={followUp} onChange={setFollowUp} required={needsFollowUp} />
              {!needsFollowUp && <span className="ld-field-hint">Optional — leave empty to keep each lead's own follow-up.</span>}
            </div>
          )}

          <label className="ld-field">
            <span>Note for the timeline<em>*</em></span>
            <textarea className="ld-textarea" value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Why these leads are moved — shown on each lead's timeline, e.g. Confirmed for Saturday's demo on the group call" />
          </label>

          {status && (
            <div className="ld-alert ld-alert-info">
              {leads.length - already} lead{leads.length - already === 1 ? "" : "s"} will move to <b>{def.label}</b>{already ? `; ${already} already ${already === 1 ? "is" : "are"} and will be skipped` : ""}.
              {" "}Each gets the messages set for this status in its course's lead settings{status === "ready_for_demo" ? ", and its demo reminders are scheduled" : ""}.
              {final ? " Their scheduled messages are cancelled." : ""}
            </div>
          )}
          <div className="ld-modal-foot" style={{ padding: "14px 0 0", borderTop: "none" }}>
            <button type="button" className="ld-btn" onClick={() => onClose(false)}>Cancel</button>
            <button type="button" className="ld-btn ld-btn-primary" disabled={!ready} onClick={() => window.confirm(`Move ${leads.length - already} lead(s) to "${def.label}" and send their messages?`) && run()}>
              {status ? `Move ${leads.length - already} lead${leads.length - already === 1 ? "" : "s"}` : "Move leads"}
            </button>
          </div>
        </>
      ) : (
        <>
          {phase === "saving" && <Progress label="Changing status…" done={progress.done} total={progress.total} />}
          {phase === "sending" && <Progress label={`Sending messages…${sending?.due ? ` ${sending.due} left` : ""}`} done={sending?.sent || 0} total={(sending?.sent || 0) + (sending?.due || 0)} />}
          <Results rows={results} okLabel={`moved to ${def?.label}`} sending={phase === "done" ? sending : null} />
          {phase === "done" && <div className="ld-modal-foot" style={{ padding: "14px 0 0", borderTop: "none" }}><button type="button" className="ld-btn ld-btn-primary" onClick={() => onClose(true)}>Done</button></div>}
        </>
      )}
    </Modal>
  );
};

// ─── Send a custom message ────────────────────────────────────────────────────
export const BulkMessageDialog = ({ leads, onClose }) => {
  const [options, setOptions] = useState(null);
  const [title, setTitle] = useState("");
  const [channels, setChannels] = useState({ whatsapp: true, email: false });
  const [error, setError] = useState("");
  const [phase, setPhase] = useState("form");      // form | queueing | sending | done
  const [results, setResults] = useState([]);
  const [sending, setSending] = useState(null);
  useEffect(() => {
    leadApi.bulkMessageOptions(leads.map((l) => l._id)).then(setOptions).catch((e) => setError(errorText(e)));
  }, [leads]);

  const msg = options?.messages.find((m) => m.title === title);
  const chosen = ["whatsapp", "email"].filter((c) => channels[c]);
  // Per course: does it have this message, and on which channels?
  const coverage = (options?.courses || []).map((c) => ({ ...c, has: msg?.courses[String(c._id)] || null }));

  const run = async () => {
    setError("");
    setPhase("queueing");
    try {
      const r = await leadApi.bulkMessage({ ids: leads.map((l) => l._id), title, channels: chosen });
      setResults(r.results);
      setPhase("sending");
      setSending(await drainQueue(setSending));
    } catch (e) {
      setError(errorText(e));
    }
    setPhase("done");
  };

  const busy = phase === "queueing" || phase === "sending";
  return (
    <Modal title={`Send a message to ${leads.length} lead${leads.length === 1 ? "" : "s"}`} onClose={() => onClose(phase !== "form")} busy={busy}>
      {error && <div className="ld-alert ld-alert-error">{error}</div>}
      {phase === "form" ? (
        !options ? <div className="ld-sub">Loading the custom messages…</div> : (
          <>
            <label className="ld-field">
              <span>Custom message<em>*</em></span>
              <select className="ld-select" value={title} onChange={(e) => setTitle(e.target.value)}>
                <option value="">Choose a message…</option>
                {options.messages.map((m) => <option key={m.title} value={m.title}>{m.title}</option>)}
              </select>
              {!options.messages.length && <span className="ld-field-hint">These leads' courses have no custom messages yet — add them in <Link to="/leads/setup?tab=automation">Lead settings → Course automation</Link>.</span>}
            </label>
            {msg && (
              <>
                <div className="ld-field">
                  <span>Send by<em>*</em></span>
                  <div className="ld-badges">
                    {[["whatsapp", "WhatsApp"], ["email", "Email"]].map(([k, name]) => (
                      <label key={k} className="ld-chip" style={{ cursor: "pointer" }}>
                        <input type="checkbox" checked={!!channels[k]} onChange={(e) => setChannels((c) => ({ ...c, [k]: e.target.checked }))} /> {name}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="ld-field">
                  <span>Courses in the selection</span>
                  <ul className="ld-bulk-coverage">
                    {coverage.map((c) => (
                      <li key={c._id}>
                        <b>{c.courseTitle}</b> · {c.leads} lead{c.leads === 1 ? "" : "s"} —{" "}
                        {!c.has ? <span className="is-bad">no "{title}" message: skipped</span>
                          : chosen.map((ch) => <span key={ch} className={c.has[ch] ? "is-ok" : "is-bad"}>{ch === "email" ? "Email" : "WhatsApp"} {c.has[ch] ? "✓" : "✗ no template"} </span>)}
                      </li>
                    ))}
                  </ul>
                  <span className="ld-field-hint">Each lead gets its own course's version of the message. Leads without a mobile number / email address are skipped for that channel.</span>
                </div>
              </>
            )}
            <div className="ld-modal-foot" style={{ padding: "14px 0 0", borderTop: "none" }}>
              <button type="button" className="ld-btn" onClick={() => onClose(false)}>Cancel</button>
              <button type="button" className="ld-btn ld-btn-primary" disabled={!msg || !chosen.length}
                onClick={() => window.confirm(`Send "${title}" to ${leads.length} lead(s) by ${chosen.map((c) => (c === "email" ? "email" : "WhatsApp")).join(" and ")}?`) && run()}>
                Send to {leads.length} lead{leads.length === 1 ? "" : "s"}
              </button>
            </div>
          </>
        )
      ) : (
        <>
          {phase === "queueing" && <Progress label="Preparing the messages…" done={0} total={0} />}
          {phase === "sending" && <Progress label={`Sending…${sending?.due ? ` ${sending.due} left` : ""}`} done={sending?.sent || 0} total={(sending?.sent || 0) + (sending?.due || 0)} />}
          <Results rows={results} okLabel="queued" sending={phase === "done" ? sending : null} />
          {phase === "done" && <div className="ld-modal-foot" style={{ padding: "14px 0 0", borderTop: "none" }}><button type="button" className="ld-btn ld-btn-primary" onClick={() => onClose(true)}>Done</button></div>}
        </>
      )}
    </Modal>
  );
};
